
import { Component, inject, OnInit, signal } from '@angular/core';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { RoleService } from '../../core/services/role.service';
import { BatchService } from '../../core/services/batch/batch.service';
import { FriendlyDatePipe } from "../../pipes/friendly-date.pipe";
import { DateTooltipDirective } from '../../directives/date-tooltip.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CustomerService } from '../../core/services/customer.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OperationTypeService } from '../../core/services/operation-type/operation-type.service';
import { IconSearchComponent } from '../../shared/components/icon-search/icon-search.component';
import { SelectComponent } from '../../shared/components/select/select.component';
import { DateFilterComponent } from '../../shared/components/date-filter/date-filter.component';
import { DataTableComponent, TableCellDirective, TableColumn } from '../../shared/components/data-table';
import { ArchiveButtonComponent } from '../../shared/components/archive-button/archive-button.component';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { InputComponent } from '../../shared/components/input/input.component';

@Component({
  selector: 'app-batch-list',
  standalone: true,
  imports: [
    RouterModule,
    FriendlyDatePipe,
    DateTooltipDirective,
    TranslatePipe,
    ReactiveFormsModule,
    IconSearchComponent,
    SelectComponent,
    DateFilterComponent,
    FormsModule,
    DataTableComponent,
    TableCellDirective,
    ArchiveButtonComponent,
    StatusBadgeComponent,
    ButtonComponent,
    ConfirmModalComponent,
    InputComponent
  ],
  templateUrl: './batch-list.component.html',
  styleUrl: './batch-list.component.css'
})
export class BatchListComponent implements OnInit {
  isAgent: boolean = false;

  get columns(): TableColumn[] {
    const cols: TableColumn[] = [
      { key: 'name', label: 'BATCH_LIST.TABLE.NAME', width: this.isAgent ? '24%' : '28%' },
      { key: 'operationTypeName', label: 'BATCH_LIST.TABLE.TYPE_OF_OPERATION', width: this.isAgent ? '22%' : '26%' },
      { key: 'createdDate', label: 'BATCH_LIST.TABLE.DATE', width: this.isAgent ? '24%' : '26%' },
      { key: 'status', label: 'BATCH_LIST.TABLE.STATUS', width: this.isAgent ? '18%' : '20%' },
    ];
    if (this.isAgent) {
      cols.push({ key: 'action', label: 'BATCH_LIST.TABLE.ACTION', width: '12%', align: 'center' });
    }
    return cols;
  }
  batchesList: any = [];
  loading: boolean = false;
  errorMsg: string = '';
  selectedCustomer: any;
  filterForm!: FormGroup;
  currentPage = signal<number>(1);
  pageSize = signal<number>(10);
  totalPages = signal<number>(0);
  lastPage = signal<boolean>(false);
  firstPage = signal<boolean>(false);
  activeFilterDropDown = signal<boolean>(false);
  isLoadingOperationTypes = signal<boolean>(false);
  operationTypes = signal<any>([]);
  private hadActiveFilters = false;

  constructor(
    private fb: FormBuilder,
    private roleService: RoleService,
    private batchService: BatchService,
    private router: Router,
    private customerService: CustomerService,
    private activateRoute: ActivatedRoute,
    private operationTypeService: OperationTypeService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.isAgent = this.roleService.isAgent();
    this.initializeFilterForm();
    this.activateRoute.queryParams.subscribe((params) => {
      const pageFromUrl = +params['page'] || 1;
      this.currentPage.set(pageFromUrl);
      this.filterForm.patchValue({
        name: params['name'] || '',
        dateStart: params['dateStart'] || '',
        dateEnd: params['dateEnd'] || '',
        operationTypeCode: params['operationTypeCode'] || ''
      }, { emitEvent: false });

      if (params['name'] || params['dateStart'] || params['dateEnd'] || params['operationTypeCode']) {
        this.activeFilterDropDown.set(true);
      }
      this.hadActiveFilters = this.hasActiveFilters();
    });
    this.customerService.selectedCustomer$.subscribe((customer) => {
      this.selectedCustomer = customer;
      this.getBatches();
      this.getOperationTypes();
    });
  }

  initializeFilterForm() {
    this.filterForm = this.fb.group({
      name: [''],
      dateStart: [''],
      dateEnd: [''],
      operationTypeCode: ['']
    });
  }

  getBatches() {
    const filters = { ...this.filterForm.value };
    filters.name = filters.name.trim();
    const currentParams = { ...this.activateRoute.snapshot.queryParams };
    Object.keys(filters).forEach(key => {
      if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
        currentParams[key] = filters[key];
      } else {
        delete currentParams[key];
      }
    });
    currentParams['page'] = this.currentPage();

    this.router.navigate([], {
      queryParams: currentParams,
    });
    this.loading = true;

    this.batchService.getAllbatches(this.currentPage() - 1, this.pageSize(), this.selectedCustomer?.id, filters).subscribe(
      (res: any) => {
        this.batchesList = res.content;
        this.totalPages.update(value => res.totalPages);
        this.lastPage.update(value => res.last);
        this.firstPage.update(value => res.first);
        this.loading = false;
      },
      (err: any) => {
        this.errorMsg = err;
        this.loading = false;
      }
    );
  }

  showDetails(batch: any) {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }
    const queryParams: any = {};
    if (this.selectedCustomer) {
      queryParams.customerId = this.selectedCustomer?.id;
    }
    this.router.navigate([`/backoffice/details-batch/${batch.id}`], { queryParams, queryParamsHandling: 'merge' });
  }

  batchToArchive: any = null;
  isArchivingBatch = false;

  openArchiveModal(batch: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.batchToArchive = batch;
  }

  closeArchiveModal(): void {
    this.batchToArchive = null;
    this.isArchivingBatch = false;
  }

  confirmArchiveBatch(): void {
    if (!this.batchToArchive) {
      return;
    }
    this.isArchivingBatch = true;
    const id = this.batchToArchive.id;
    this.batchesList = this.batchesList.filter((b: any) => b.id !== id);
    this.isArchivingBatch = false;
    this.closeArchiveModal();
  }

  updateCurrentPage(page: number) {
    this.currentPage.set(page);
    this.getBatches()
  }

  toggleDropdown() {
    this.activeFilterDropDown.update((value) => !value);
  }

  getOperationTypes() {
    this.isLoadingOperationTypes.set(true);
    this.operationTypeService.getAllOperationTypes(this.selectedCustomer?.id, true).subscribe(
      (res: any) => {
        this.operationTypes.set(res);
        this.isLoadingOperationTypes.set(false);
      },
      (err: any) => {
        this.isLoadingOperationTypes.set(false);
        this.errorMsg = err;
      }
    );
  }

  applyFilters() {
    this.currentPage.set(1);
    this.hadActiveFilters = this.hasActiveFilters();
    this.getBatches();
  }

  resetFilters() {
    this.hadActiveFilters = false;
    this.filterForm.reset({
      name: '',
      dateStart: '',
      dateEnd: '',
      operationTypeCode: ''
    });
    this.applyFilters();
  }

  getActiveFilterChips(): { prefix: string; value: string; remove: () => void }[] {
    if (!this.filterForm) return [];
    const val = this.filterForm.value;
    const chips: { prefix: string; value: string; remove: () => void }[] = [];

    if (val.name && val.name.trim() !== '') {
      chips.push({
        prefix: this.translate.instant('REQUESTLIST.FILTERS.SEARCH'),
        value: val.name.trim(),
        remove: () => {
          this.filterForm.patchValue({ name: '' });
          this.applyFilters();
        }
      });
    }

    if (val.operationTypeCode) {
      const opCodes = Array.isArray(val.operationTypeCode) ? val.operationTypeCode : [val.operationTypeCode];
      opCodes.forEach((opCode: number) => {
        const found = this.operationTypes().find((op: any) => op.code === opCode);
        const labelText = found?.name || '';
        chips.push({
          prefix: this.translate.instant('REQUESTLIST.FILTERS.OPERATION_TYPE') + ':',
          value: labelText,
          remove: () => this.removeFilterItem('operationTypeCode', opCode)
        });
      });
    }

    if (val.dateStart || val.dateEnd) {
      let dateStr = '';
      if (val.dateStart && val.dateEnd) {
        dateStr = `${val.dateStart} ${this.translate.instant('REQUESTLIST.FILTERS.TO')} ${val.dateEnd}`;
      } else if (val.dateStart) {
        dateStr = `${this.translate.instant('REQUESTLIST.FILTERS.SINCE')} ${val.dateStart}`;
      } else {
        dateStr = `${this.translate.instant('REQUESTLIST.FILTERS.UNTIL')} ${val.dateEnd}`;
      }
      chips.push({
        prefix: "Date :",
        value: dateStr,
        remove: () => {
          this.filterForm.patchValue({ dateStart: '', dateEnd: '' });
          this.applyFilters();
        }
      });
    }

    return chips;
  }

  removeFilterItem(controlName: string, value: any) {
    const current = this.filterForm.get(controlName)?.value;
    if (Array.isArray(current)) {
      const updated = current.filter((v: any) => v !== value);
      this.filterForm.patchValue({ [controlName]: updated.length > 0 ? updated : [] });
    } else {
      this.filterForm.patchValue({ [controlName]: null });
    }
    this.applyFilters();
  }

  checkResetIfEmpty() {
    if (!this.hasActiveFilters()) {
      if (this.hadActiveFilters) {
        this.resetFilters();
      }
    } else {
      this.hadActiveFilters = true;
    }
  }

  hasActiveFilters(): boolean {
    if (!this.filterForm) return false;
    const val = this.filterForm.value;
    const nameVal = val.name && val.name.trim() !== '';
    const dateStartVal = !!val.dateStart;
    const dateEndVal = !!val.dateEnd;
    const opTypeVal = Array.isArray(val.operationTypeCode) ? val.operationTypeCode.length > 0 : !!val.operationTypeCode;

    return !!(nameVal || dateStartVal || dateEndVal || opTypeVal);
  }
}
